import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { chatErrorMessage, useChat } from '../../lib/chat';
import { useSession, useToast } from '../../lib/store';
import type { ChatAuthor, ChatMessage } from '../../lib/types';
import { ChatMessageBody } from './ChatMessageBody';
import { PlayerMenu } from './PlayerMenu';
import { RoleTags } from '../roles/RoleBadge';

/**
 * The chat dock.
 *
 * Bottom-right on desktop, a floating bubble that opens full-screen on a phone.
 *
 * It floats *over* the page and never reflows it. Reserving a gutter was worse
 * than the problem it solved: opening chat re-laid-out the whole screen,
 * dropping the Team to narrower cards and the stat grid from four columns
 * to two, so glancing at chat rearranged the thing you were looking at.
 *
 * Both states stay mounted and cross-fade, rather than one unmounting the
 * other. That is what makes closing animate at all — an unmounted panel cannot
 * transition — and it keeps the scroll position and the message buffer intact
 * across a minimise.
 */
export function ChatDock() {
  const chat = useChat();
  const { me } = useSession();
  const { push } = useToast();

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The menu remembers which message it was opened from, so "Report" in the
  // player menu reports something concrete rather than a bare accusation.
  const [menu, setMenu] = useState<
    { author: ChatAuthor; messageId: string; left: number; top: number } | null
  >(null);

  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Only auto-scroll when the reader is already at the bottom. */
  const pinnedToBottom = useRef(true);

  const active = chat.channels.find((channel) => channel.key === chat.activeKey) ?? null;

  useLayoutEffect(() => {
    const log = logRef.current;
    if (!log || !pinnedToBottom.current) return;
    log.scrollTop = log.scrollHeight;
  }, [chat.messages, chat.open]);

  // Reading someone's scroll position rather than always jumping: yanking the
  // view to the bottom while they are reading back is the single most annoying
  // thing a chat window can do.
  const onScroll = () => {
    const log = logRef.current;
    if (!log) return;
    pinnedToBottom.current = log.scrollHeight - log.scrollTop - log.clientHeight < 60;
  };

  useEffect(() => {
    if (chat.open) inputRef.current?.focus();
  }, [chat.open, chat.activeKey]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;

    setSending(true);
    setError(null);
    try {
      await chat.send(body);
      setDraft('');
      pinnedToBottom.current = true;
    } catch (caught) {
      setError(chatErrorMessage(caught));
    } finally {
      setSending(false);
    }
  }

  async function block(userId: string) {
    try {
      await chat.blockUser(userId, true);
      push('info', 'Blocked. You will not see their messages.');
    } catch {
      push('error', 'Could not block that trainer.');
    }
  }

  async function report(id: string) {
    try {
      await chat.reportMessage(id, '');
      push('success', 'Reported. Thank you — a moderator will look.', '⚑');
    } catch {
      push('error', 'Could not report that message.');
    }
  }

  async function remove(id: string) {
    try {
      await chat.removeMessage(id);
    } catch {
      push('error', 'Could not remove that message.');
    }
  }

  if (!me) return null;

  const visible = chat.channels.filter((channel) => !channel.hidden);
  const open = chat.open;

  return (
    <>
      {/* Both states are always mounted so each can animate out. `inert` and
          `aria-hidden` are what actually take the hidden one out of the tab
          order and the accessibility tree — opacity alone would leave a closed
          panel focusable behind the page. */}
      <button
        type="button"
        className={`chat-bubble ${open ? 'chat-bubble--hidden' : ''}`}
        onClick={() => chat.setOpen(true)}
        aria-label={chat.totalUnread > 0 ? `Open chat, ${chat.totalUnread} unread` : 'Open chat'}
        aria-hidden={open}
        tabIndex={open ? -1 : undefined}
      >
        <span className="chat-bubble__icon" aria-hidden>
          ✎
        </span>
        <span className="chat-bubble__label">Chat</span>
        {chat.totalUnread > 0 && (
          <span className="chat-bubble__badge">
            {chat.totalUnread > 99 ? '99+' : chat.totalUnread}
          </span>
        )}
      </button>

      <section
        className={`pxframe chat-dock ${open ? '' : 'chat-dock--closed'}`}
        aria-label="Chat"
        aria-hidden={!open}
        {...(open ? {} : { inert: '' })}
      >
        <header className="chat-dock__head">
          <span
            className={`chat__dot ${chat.connected ? '' : 'chat__dot--off'}`}
            title={chat.connected ? 'Connected' : 'Reconnecting…'}
            aria-hidden
          />
          <span className="chat-dock__title">{active?.name ?? 'Chat'}</span>
          <span className="spacer" />
          <span className="tiny muted" title="Trainers connected">
            {chat.online} online
          </span>
          <button
            type="button"
            className="chat-dock__close"
            onClick={() => chat.setOpen(false)}
            aria-label="Minimise chat"
          >
            ─
          </button>
        </header>

        <nav className="chat-tabs" aria-label="Channels">
          {visible.map((channel) => (
            <button
              key={channel.key}
              type="button"
              className={`chat-tab ${channel.key === chat.activeKey ? 'chat-tab--active' : ''}`}
              onClick={() => chat.selectChannel(channel.key)}
              title={channel.description}
            >
              <span aria-hidden>{channel.icon}</span>
              <span className="chat-tab__name">{channel.name}</span>
              {channel.unread > 0 && channel.key !== chat.activeKey && (
                <span className="chat-tab__badge">
                  {channel.unread > 99 ? '99+' : channel.unread}
                </span>
              )}
            </button>
          ))}
        </nav>

        {chat.pinned.length > 0 && (
          <div className="chat-pinned">
            {chat.pinned.map((message) => (
              <div key={message.id} className="chat-pinned__row">
                <span aria-hidden>📌</span>
                <span className="chat-pinned__body">
                  <ChatMessageBody tokens={message.tokens} />
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="chat__log" ref={logRef} onScroll={onScroll} role="log" aria-live="polite">
          {chat.loadingHistory ? (
            <div className="chat__empty tiny muted">Loading…</div>
          ) : chat.messages.length === 0 ? (
            <div className="chat__empty tiny muted">
              {active?.kind === 'GUILD'
                ? 'Nothing said here yet. Say hello to your guild.'
                : 'Quiet in here. Be the first to say something.'}
            </div>
          ) : (
            chat.messages.map((message) => (
              <MessageRow
                key={message.id}
                message={message}
                isMine={message.author?.id === me.user.id}
                canModerate={chat.canModerate}
                onOpenMenu={(author, element) => {
                  const rect = element.getBoundingClientRect();
                  setMenu({
                    author,
                    messageId: message.id,
                    left: Math.max(8, Math.min(window.innerWidth - 230, rect.left)),
                    top: Math.max(8, rect.top - 8),
                  });
                }}
                onRemove={() => void remove(message.id)}
                onPin={() => void chat.pinMessage(message.id, !message.isPinned)}
                onReport={() => void report(message.id)}
              />
            ))
          )}
        </div>

        {error && <div className="chat__error tiny">{error}</div>}

        <form className="chat__compose" onSubmit={submit}>
          <input
            ref={inputRef}
            className="input"
            value={draft}
            maxLength={chat.maxLength}
            onChange={(event) => setDraft(event.target.value)}
            disabled={!active || !active.canPost || (active.isLocked && !chat.canModerate)}
            placeholder={
              !active
                ? 'Loading…'
                : active.isLocked && !chat.canModerate
                  ? `${active.name} is read-only.`
                  : !active.canPost
                    ? `Opens at Trainer Level ${active.minTrainerLevel}.`
                    : `Message ${active.name}…`
            }
            aria-label={`Message ${active?.name ?? 'chat'}`}
          />
          <button
            className="btn btn--sm btn--go"
            type="submit"
            disabled={sending || draft.trim().length === 0}
          >
            Send
          </button>
        </form>
      </section>

      {menu && open && (
        <PlayerMenu
          author={menu.author}
          isSelf={menu.author.id === me.user.id}
          position={{ left: menu.left, top: menu.top }}
          canInvite={Boolean(me.guild && me.guild.role !== 'MEMBER')}
          onClose={() => setMenu(null)}
          onBlock={() => void block(menu.author.id)}
          onReport={() => void report(menu.messageId)}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------------------
   One line
   ------------------------------------------------------------------------- */

function MessageRow({
  message,
  isMine,
  canModerate,
  onOpenMenu,
  onRemove,
  onPin,
  onReport,
}: {
  message: ChatMessage;
  isMine: boolean;
  canModerate: boolean;
  onOpenMenu: (author: ChatAuthor, element: HTMLElement) => void;
  onRemove: () => void;
  onPin: () => void;
  onReport: () => void;
}) {
  // A line the game wrote. No author, no menu, no moderation — it renders as
  // narration so it reads differently from conversation at a glance.
  if (message.kind === 'SYSTEM') {
    return (
      <div className="chat__line chat__line--system">
        <span className="chat__body">
          <ChatMessageBody tokens={message.tokens} />
        </span>
      </div>
    );
  }

  const time = new Date(message.createdAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      className={[
        'chat__line',
        message.deleted ? 'chat__line--deleted' : '',
        message.mentionsMe ? 'chat__line--mention' : '',
        message.kind === 'ANNOUNCEMENT' ? 'chat__line--announce' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {message.author ? (
        <button
          type="button"
          className={`chat__who ${isMine ? 'chat__who--me' : ''}`}
          onClick={(event) => onOpenMenu(message.author!, event.currentTarget)}
          title={`${message.author.trainerName} · ${message.author.title || 'Trainer'}`}
        >
          {message.author.guildRole === 'LEADER' && <span aria-hidden>★ </span>}
          {message.author.guildRole === 'OFFICER' && <span aria-hidden>◈ </span>}
          <RoleTags roles={message.author.roles ?? []} />
          {message.author.username}
          {message.author.isNpc && (
            <span className="chip chip--npc chip--sm" style={{ marginLeft: 3 }}>
              🌿
            </span>
          )}
        </button>
      ) : (
        <span className="chat__who chat__who--gone">—</span>
      )}

      <span className="chat__body">
        <ChatMessageBody tokens={message.tokens} />
      </span>

      {!message.deleted && (
        <span className="chat__tools">
          {canModerate && (
            <button type="button" className="chat__tool" onClick={onPin} title="Pin">
              📌
            </button>
          )}
          {(canModerate || isMine) && (
            <button type="button" className="chat__tool" onClick={onRemove} title="Remove">
              ✕
            </button>
          )}
          {!isMine && (
            <button type="button" className="chat__tool" onClick={onReport} title="Report">
              ⚑
            </button>
          )}
        </span>
      )}

      <span className="chat__time">{time}</span>
    </div>
  );
}
